"use client";

import * as React from "react";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { Select as SelectPrimitive } from "@base-ui/react/select";

import { cx } from "../../cx";
import { buildSelectLabelMap, SelectLabelMapContext } from "./select-label-map";
import styles from "./select.module.css";

export type SelectTriggerSize = "sm" | "default" | "lg";

type WithClassName<P> = Omit<P, "className"> & { className?: string };

function Select<T = string>({
  value,
  defaultValue,
  onValueChange,
  children,
  ...props
}: Omit<
  SelectPrimitive.Root.Props<T>,
  "value" | "defaultValue" | "onValueChange"
> & {
  value?: T;
  defaultValue?: T;
  onValueChange?: (value: T) => void;
}) {
  const labelMap = React.useMemo(
    () => buildSelectLabelMap(children, SelectItem),
    [children],
  );
  return (
    <SelectLabelMapContext.Provider value={labelMap}>
      <SelectPrimitive.Root
        data-slot="select"
        value={value}
        defaultValue={defaultValue}
        onValueChange={
          onValueChange as SelectPrimitive.Root.Props<T>["onValueChange"]
        }
        {...props}
      >
        {children}
      </SelectPrimitive.Root>
    </SelectLabelMapContext.Provider>
  );
}

type SelectValueProps = Omit<
  SelectPrimitive.Value.Props,
  "children" | "className"
> & {
  className?: string;
  children?:
    | React.ReactNode
    | ((value: unknown, label: string) => React.ReactNode);
  placeholder?: React.ReactNode;
};

function SelectValue({
  children,
  placeholder,
  className,
  ...props
}: SelectValueProps) {
  const labelMap = React.useContext(SelectLabelMapContext);
  const valueProps = {
    "data-slot": "select-value",
    className: cx(styles.value, className),
    ...(props as SelectPrimitive.Value.Props),
  };

  if (typeof children === "function") {
    return (
      <SelectPrimitive.Value {...valueProps}>
        {children as never}
      </SelectPrimitive.Value>
    );
  }

  const renderValue = (value: unknown, label: string): React.ReactNode => {
    if (typeof value === "string" && value && labelMap?.has(value)) {
      return labelMap.get(value);
    }
    if (label) return label;
    if (children !== undefined && children !== null) return children;
    return placeholder ?? null;
  };

  return (
    <SelectPrimitive.Value {...valueProps}>
      {renderValue as never}
    </SelectPrimitive.Value>
  );
}

function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: WithClassName<SelectPrimitive.Trigger.Props> & {
  size?: SelectTriggerSize;
}) {
  return (
    <SelectPrimitive.Trigger
      data-tui=""
      data-slot="select-trigger"
      data-size={size}
      className={cx(styles.trigger, className)}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon className={styles.icon}>
        <ChevronDownIcon />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

type SelectContentProps = WithClassName<SelectPrimitive.Popup.Props> &
  Pick<
    SelectPrimitive.Positioner.Props,
    "side" | "align" | "sideOffset" | "alignOffset"
  > & {
    position?: "item-aligned" | "popper";
    /** `guide` caps the list at 216px; `viewport` only at the viewport edge. */
    listHeight?: "guide" | "viewport";
    container?: HTMLElement | null;
  };

function SelectContent({
  className,
  children,
  position = "popper",
  listHeight = "guide",
  align = "start",
  side = "bottom",
  sideOffset = 6,
  alignOffset = 0,
  container,
  ...props
}: SelectContentProps) {
  return (
    <SelectPrimitive.Portal container={container ?? undefined}>
      <SelectPrimitive.Positioner
        data-slot="select-positioner"
        align={align}
        side={side}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        alignItemWithTrigger={position === "item-aligned"}
        className={styles.positioner}
      >
        <SelectPrimitive.Popup
          data-tui=""
          data-slot="select-content"
          className={cx(styles.popup, className)}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List
            data-slot="select-list"
            data-list-height={listHeight}
            className={styles.list}
          >
            {children}
          </SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}

function SelectScrollUpButton() {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={styles.scrollArrow}
    >
      <ChevronUpIcon />
    </SelectPrimitive.ScrollUpArrow>
  );
}

function SelectScrollDownButton() {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={styles.scrollArrow}
    >
      <ChevronDownIcon />
    </SelectPrimitive.ScrollDownArrow>
  );
}

function SelectItem({
  className,
  children,
  ...props
}: WithClassName<SelectPrimitive.Item.Props>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cx(styles.item, className)}
      {...props}
    >
      <span data-slot="select-item-indicator" className={styles.indicator}>
        <SelectPrimitive.ItemIndicator>
          <CheckIcon />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

export { Select, SelectContent, SelectItem, SelectTrigger, SelectValue };
